import React, { useState, useEffect } from 'react';
import {
  Container,
  Stack,
  Typography,
  TextField,
  Autocomplete,
  Card,
  Box,
  Grid,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../iconify/Iconify';
import api from '../../api';
import { toast } from 'react-toastify';

const PAYMENT_TYPES = [
  { label: 'Credit', value: 'Credit' },
  { label: 'To Pay', value: 'To Pay' },
  { label: 'Both', value: 'Both' },
  { label: 'Cash', value: 'Cash' },
];

const GenerateBillComponent = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState({
    dateFrom: '',
    dateTo: '',
    paymentType: 'Credit',
    clientId: '',
    clientName: '',
    sendEmail: true,
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await api.get('/api/v1/clients');
      if (response.data.success) {
        const clientsData = response.data.clients.map((item) => ({
          label: `${item.name} (${item.gstNo})`,
          value: item,
          id: item.id,
        }));
        clientsData.push({ label: 'General', value: null, id: 'General' });
        setClients(clientsData);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Something went wrong!');
    }
  };

  const onChangeSearch = (event) => {
    const { name, value, type, checked } = event.target;
    setSearch((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const downloadPdf = (pdfBase64) => {
    const byteCharacters = atob(pdfBase64);
    const byteNumbers = new Array(byteCharacters.length)
      .fill()
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Billing_${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const onClickGenerateBill = async () => {
    if (!search.clientId) {
      toast.error('Please select a client.');
      return;
    }
    if (!search.dateFrom || !search.dateTo) {
      toast.error('Please select both date range fields.');
      return;
    }
    if (search.dateFrom > search.dateTo) {
      toast.error('Date From cannot be after Date To.');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/api/v1/billing', search);

      if (response.data.success) {
        toast.success(response.data.message);

        const pdfBase64 = response.data.pdf;
        if (pdfBase64) {
          downloadPdf(pdfBase64);
        }
      } else if (response.data.success === false) {
        toast.error(response.data.message);
      } else {
        toast.error('Something went wrong!');
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to generate bill.');
    } finally {
      setLoading(false);
    }
  };

  const onClientChange = (event, values) => {
    if (!values) return;
    setSearch((prev) => ({ ...prev, clientId: values.id, clientName: values.label }));
  };

  const onClickPaymentType = (event, values) => {
    if (!values) return;
    setSearch((prev) => ({ ...prev, paymentType: values.value }));
  };

  const selectedClient = clients.find((client) => client.label === search.clientName) || null;
  const selectedPaymentType =
    PAYMENT_TYPES.find((type) => type.value === search.paymentType) || PAYMENT_TYPES[0];

  return (
    <Container maxWidth="lg">
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
            Generate Bill
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create billing statements for a client within a selected date range.
          </Typography>
        </Box>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 2.5,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'rgba(25,118,210,0.1)',
            color: 'primary.main',
          }}
        >
          <Iconify icon="solar:bill-list-bold-duotone" width={28} />
        </Box>
      </Stack>

      <Card
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'rgba(25,118,210,0.14)',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.98), rgba(246,251,255,0.9))',
          boxShadow: '0 12px 28px rgba(16, 24, 40, 0.06)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2.5, bgcolor: 'rgba(25,118,210,0.06)' }}>
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(25,118,210,0.14)',
                color: 'primary.main',
              }}
            >
              <Iconify icon="solar:document-add-bold-duotone" width={18} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Bill Configuration
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Fill in the details below to generate a PDF bill
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Autocomplete
                disablePortal
                id="client-autocomplete"
                options={clients}
                onChange={onClientChange}
                fullWidth
                value={selectedClient}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, value) => option.label === value?.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Client"
                    placeholder="Search by name or GST number"
                    sx={{ bgcolor: 'background.paper' }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Date From"
                name="dateFrom"
                type="date"
                variant="outlined"
                fullWidth
                value={search.dateFrom}
                InputLabelProps={{ shrink: true }}
                onChange={onChangeSearch}
                sx={{ bgcolor: 'background.paper' }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Date To"
                name="dateTo"
                type="date"
                variant="outlined"
                fullWidth
                value={search.dateTo}
                InputLabelProps={{ shrink: true }}
                onChange={onChangeSearch}
                sx={{ bgcolor: 'background.paper' }}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                disablePortal
                disableClearable
                id="payment-type-autocomplete"
                options={PAYMENT_TYPES}
                onChange={onClickPaymentType}
                onKeyDown={(e) => e.preventDefault()}
                fullWidth
                value={selectedPaymentType}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, value) => option.label === value?.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Payment Type"
                    sx={{ bgcolor: 'background.paper' }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: search.sendEmail ? 'rgba(25,118,210,0.25)' : 'divider',
                bgcolor: search.sendEmail ? 'rgba(25,118,210,0.04)' : 'background.paper',
                transition: 'border-color 0.2s ease, background-color 0.2s ease',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    name="sendEmail"
                    checked={search.sendEmail}
                    onChange={onChangeSearch}
                    color="primary"
                  />
                }
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify icon="solar:letter-bold-duotone" width={20} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Send Email
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Email the bill to the client after generation
                      </Typography>
                    </Box>
                  </Stack>
                }
                sx={{ m: 0, alignItems: 'flex-start' }}
              />
            </Box>

            <LoadingButton
              variant="contained"
              size="large"
              loading={loading}
              onClick={onClickGenerateBill}
              startIcon={<Iconify icon="solar:download-minimalistic-bold" />}
              sx={{
                minWidth: { xs: '100%', sm: 200 },
                borderRadius: 2,
                py: 1.4,
                fontWeight: 700,
                background: 'linear-gradient(120deg, #1976d2, #0d47a1)',
                boxShadow: '0 8px 18px rgba(25,118,210,0.32)',
                '&:hover': {
                  background: 'linear-gradient(120deg, #1565c0, #0b3d91)',
                  boxShadow: '0 10px 22px rgba(25,118,210,0.38)',
                },
              }}
            >
              Generate Bill
            </LoadingButton>
          </Stack>

          <Alert
            severity="info"
            icon={<Iconify icon="solar:info-circle-bold-duotone" width={22} />}
            sx={{ mt: 3, borderRadius: 2 }}
          >
            The bill PDF will download automatically. When &quot;Send Email&quot; is enabled, a copy
            will also be sent to the client&apos;s registered email address.
          </Alert>
        </Box>
      </Card>
    </Container>
  );
};

export default GenerateBillComponent;
